import { InfiniteData } from "@tanstack/react-query";
import { UseTellescopeInfiniteQueryOptions, UseTellescopeInfiniteQueryResult } from "../../types";
import { useTellescopeInfiniteQuery } from "../../use-tellescope-infinite-query";

/** A single Twilio SMS message, as returned by the `sms-logs` proxy_read endpoint. */
export interface SMSLogEntry {
  /** Twilio Message SID */
  sid: string;
  /** Sender phone number (E.164) */
  from: string;
  /** Recipient phone number (E.164) */
  to: string;
  /** Twilio message status (queued, sent, delivered, failed, ...) */
  status: string;
  /** inbound | outbound-api | outbound-call | outbound-reply */
  direction: string;
  /** ISO timestamp (Twilio dateSent) */
  dateSent: string;
}

export interface TwilioLogsQueryRequest {
  /** ISO date lower bound (Twilio `dateSentAfter`). */
  from?: string;
  /** ISO date upper bound (Twilio `dateSentBefore`). */
  to?: string;
  /** Defaults to 50; clamped to a max of 100 server-side. */
  pageSize?: number;
}

export interface TwilioSmsLogsResponse {
  data: {
    logs: SMSLogEntry[];
    /** null === last page */
    nextPageUrl: string | null;
    hasMore: boolean;
  };
}

/** Result of {@link useTwilioSmsLogsQuery}'s `select`: flattened entries plus the oldest timestamp on the last loaded page (used for cross-stream merge watermarking). */
export interface TwilioSmsLogsSelection {
  logs: SMSLogEntry[];
  /** ms epoch of the oldest entry on the most recently loaded page, or null if no entries loaded. */
  lastPageOldest: number | null;
}

const oldestOfPage = (page: SMSLogEntry[]): number | null => {
  if (page.length === 0) return null;
  return page.reduce((min, entry) => Math.min(min, new Date(entry.dateSent).getTime()), Infinity);
};

const selectSmsLogs = (data: InfiniteData<TwilioSmsLogsResponse, string | null>): TwilioSmsLogsSelection => ({
  logs: data.pages.flatMap((page) => page.data.logs),
  lastPageOldest: oldestOfPage(data.pages.at(-1)?.data.logs ?? []),
});

export interface UseTwilioSmsLogsQueryOptions<TError = Error>
  extends Omit<
    UseTellescopeInfiniteQueryOptions<
      "integrations",
      "proxy_read",
      string | null,
      TError,
      TwilioSmsLogsSelection
    >,
    "model" | "method" | "request" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
  > {
  request?: TwilioLogsQueryRequest;
}

/**
 * Infinite query over Twilio SMS logs via `integrations.proxy_read` (`type: "sms-logs"`).
 *
 * Cursor pagination uses Twilio's `nextPageUrl`, packed into the JSON `query` field. The `select`
 * output preserves `lastPageOldest` so {@link useTwilioPhoneLogsQuery} can merge this stream with
 * the call-logs stream while keeping global date ordering correct.
 */
export const useTwilioSmsLogsQuery = <TError = Error>({
  request,
  ...options
}: UseTwilioSmsLogsQueryOptions<TError> = {}): UseTellescopeInfiniteQueryResult<
  "integrations",
  "proxy_read",
  TError,
  TwilioSmsLogsSelection
> =>
  useTellescopeInfiniteQuery<"integrations", "proxy_read", string | null, TError, TwilioSmsLogsSelection>({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Twilio",
      type: "sms-logs",
      query: request && Object.values(request).some((v) => v !== undefined) ? JSON.stringify(request) : undefined,
    },
    initialPageParam: "",
    getNextPageParam: (lastPage: TwilioSmsLogsResponse) => lastPage.data.nextPageUrl ?? undefined,
    paginateRequest: (cursor, { query, ...rest }) => {
      if (!cursor) return { ...rest, query };
      return { ...rest, query: JSON.stringify({ nextPageUrl: cursor }) };
    },
    select: selectSmsLogs,
    ...options,
  });
